import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Maximize2, ChevronRight as ChevRight,
  Heart, MessageCircle, Share2, CalendarDays, HelpCircle,
  Gauge, Flame, Cog, Wrench, Car, Palette, Compass,
  ShieldCheck, MapPin, Clock, Hammer, FileText, Truck,
  CreditCard, Lock, Info, Plus, Minus, ArrowRight,
} from 'lucide-react';
import { product, usd } from './productMockData';
import TopNav from './TopNav';

// ======================================================================
//   PRODUCT / AUCTION PAGE
//   Same DNA as the Build Showcase — white surfaces, burgundy accent,
//   Chakra Petch + Geist + JetBrains Mono.  Mirrors the existing product
//   layout (hero gallery + auction sidebar, highlights, vehicle details,
//   modifications, condition report, media, Q&A, comments, purchase
//   info, conditions of business) — restyled with our design language.
// ======================================================================

const fmt = (n) => Number(n).toLocaleString('en-US');

// Map highlight icon keys → Lucide components
const highlightIcon = {
  gauge:        Gauge,
  flame:        Flame,
  torque:       Wrench,
  engine:       Cog,
  transmission: Cog,
  drive:        Compass,
  paint:        Palette,
  car:          Car,
};

// =========== Action button (heart, comment, share, etc.) ============
const ActionBtn = ({ icon: Icon, label, testId, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="flex-1 flex items-center justify-center gap-2 py-3 px-2 rounded-xl border border-[var(--border)] hover:border-[var(--border-bright)] hover:bg-[var(--bg-subtle)] transition text-sm text-[var(--text-dim)] hover:text-[var(--ink)]"
  >
    <Icon size={15} />
    {label && <span className="font-display tracking-widest text-[10px] uppercase">{label}</span>}
  </button>
);

// =========== Breadcrumb =====================================
const Breadcrumb = () => (
  <nav className="max-w-[1440px] mx-auto px-6 pt-6 flex items-center gap-2 text-xs font-mono text-[var(--text-mute)]" data-testid="breadcrumb">
    <Link to="/" className="hover:text-[var(--ink)] transition">Home</Link>
    <ChevRight size={12} />
    <Link to="/shop" className="hover:text-[var(--ink)] transition">Shop</Link>
    <ChevRight size={12} />
    <span className="text-[var(--ink)]">{product.year} {product.make} {product.model}</span>
  </nav>
);

// =========== Title block (above hero) ========================
const TitleBlock = () => (
  <section className="max-w-[1440px] mx-auto px-6 mt-6">
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
      <div>
        <div className="section-label mb-3">{product.subModel}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.02] tracking-tight text-[var(--ink)]">
          <span className="text-[var(--text-mute)]">{product.make.toUpperCase()}</span>{' '}
          <span>{product.title.toUpperCase()}</span>
        </h1>
        <div className="mt-2 font-display text-sm font-semibold tracking-[0.24em] text-[var(--accent)] uppercase">
          {product.buildName}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="chip chip-accent" data-testid="modification-status">
          <Hammer size={11} /> Modification status · {product.modificationStatus}
        </span>
        <span className="chip">
          <MessageCircle size={11} /> Comments · {product.comments}
        </span>
        <span className="chip">
          <HelpCircle size={11} /> Q&amp;A · {product.qa}
        </span>
      </div>
    </div>

    {/* Vehicle spec plate — Make / Model / Sub-Model / Year */}
    <div className="mt-6 inline-grid grid-cols-2 md:grid-cols-4 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] overflow-hidden">
      {[
        ['Make',      product.make],
        ['Model',     product.title],
        ['Sub-Model', product.model],
        ['Year',      product.year],
      ].map(([k, v], idx) => (
        <div
          key={k}
          className={`px-5 py-3 ${idx > 0 ? 'md:border-l' : ''} ${idx >= 2 ? 'border-t md:border-t-0' : ''} ${idx === 1 ? 'border-l md:border-l' : ''} border-[var(--border)]`}
        >
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">{k}</div>
          <div className="font-display text-base font-semibold text-[var(--ink)] leading-tight mt-0.5">{v}</div>
        </div>
      ))}
    </div>
  </section>
);

// =========== HERO + AUCTION SIDEBAR =========================
const HeroSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const next = () => setActiveIdx((i) => (i + 1) % product.gallery.length);
  const prev = () => setActiveIdx((i) => (i - 1 + product.gallery.length) % product.gallery.length);
  const current = product.gallery[activeIdx];

  return (
    <section className="max-w-[1440px] mx-auto px-6 mt-8" data-testid="hero-section">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_440px] gap-6">
        {/* LEFT — Gallery */}
        <div className="space-y-3 min-w-0">
          <div className="relative w-full aspect-[16/10] max-h-[58vh] rounded-2xl overflow-hidden bg-[var(--bg-subtle)] border border-[var(--border)]">
            <img
              src={current.src}
              alt={current.label}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
              data-testid="hero-image"
            />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

            {/* Nav arrows */}
            <button onClick={prev} data-testid="hero-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white flex items-center justify-center border border-[var(--border)] shadow transition">
              <ChevronLeft size={20} className="text-[var(--ink)]" />
            </button>
            <button onClick={next} data-testid="hero-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white flex items-center justify-center border border-[var(--border)] shadow transition">
              <ChevronRight size={20} className="text-[var(--ink)]" />
            </button>

            {/* Top-left badge */}
            <div className="absolute top-5 left-5 chip bg-white/95 backdrop-blur shadow-sm">
              <Flame size={12} className="text-[var(--accent)]" /> Featured Listing
            </div>

            {/* Bottom caption */}
            <div className="absolute bottom-5 left-5 flex items-center gap-3 text-xs text-white">
              <span className="font-mono bg-black/60 backdrop-blur px-2 py-1 rounded">
                {activeIdx + 1} / {product.gallery.length}
              </span>
              <span className="font-mono bg-black/60 backdrop-blur px-2 py-1 rounded">
                {current.label}
              </span>
            </div>

            {/* Bottom-right: expand */}
            <button className="absolute bottom-5 right-5 w-10 h-10 rounded-lg bg-black/80 hover:bg-black text-white flex items-center justify-center backdrop-blur">
              <Maximize2 size={16} />
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
            {product.gallery.map((g, i) => (
              <button
                key={g.src}
                onClick={() => setActiveIdx(i)}
                data-testid={`hero-thumb-${i}`}
                className={`thumb ${i === activeIdx ? 'active' : ''}`}
              >
                <img src={g.src} alt={g.label} />
              </button>
            ))}
          </div>

          {/* Action button row — Q&A · Comments · Share · Calendar · Save */}
          <div className="flex items-center gap-2 pt-1">
            <ActionBtn icon={HelpCircle}    label="Q&A"      testId="action-qa" />
            <ActionBtn icon={MessageCircle} label="Comment"  testId="action-comment" />
            <ActionBtn icon={Share2}        label="Share"    testId="action-share" />
            <ActionBtn icon={CalendarDays}  label="Schedule" testId="action-schedule" />
            <ActionBtn icon={Heart}         label="Save"     testId="action-save" />
          </div>
        </div>

        {/* RIGHT — Auction sidebar */}
        <AuctionSidebar />
      </div>
    </section>
  );
};

// =========== Auction sidebar =================================
const AuctionSidebar = () => {
  const navigate = useNavigate();
  const a = product.auction;

  return (
    <aside className="space-y-4" data-testid="auction-sidebar">
      {/* Auction status panel */}
      <div className="card p-5 space-y-3.5">
        <div className="flex items-baseline justify-between">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)] flex items-center gap-1.5">
            <Clock size={11} /> Auction Closes
          </div>
          <span className="chip chip-accent text-[10px]">
            {a.daysRemaining} days remaining
          </span>
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)] mb-1">
            Current Bid
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-4xl font-bold text-[var(--ink)] leading-none" data-testid="current-bid">
              {usd(a.currentBid)}
            </span>
            <span className="font-mono text-xs text-[var(--text-dim)]">
              ({a.bidsCount} Bids)
            </span>
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 chip-amber chip text-[10px]">
            <Lock size={10} /> {a.reserveDisclosure}
          </div>
        </div>

        <button
          data-testid="register-to-bid"
          className="w-full bg-[var(--accent)] hover:bg-[#6f0c0c] text-white font-display font-semibold tracking-[0.18em] uppercase text-sm py-3.5 rounded-xl transition"
        >
          Register to Bid
        </button>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--border)]">
          <button className="flex items-center gap-2 text-xs text-[var(--text-dim)] hover:text-[var(--ink)] transition">
            <Info size={13} className="text-[var(--accent)]" /> How Bidding Works
          </button>
          <button className="flex items-center gap-2 text-xs text-[var(--text-dim)] hover:text-[var(--ink)] transition justify-end">
            Bid Increment Table <Info size={13} className="text-[var(--accent)]" />
          </button>
        </div>
      </div>

      {/* Build link card */}
      <button
        onClick={() => navigate(`/build/${product.slug}/${product.buildId}`)}
        data-testid="view-build-link"
        className="w-full card card-hover p-4 flex items-center gap-4 text-left transition group"
      >
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-[var(--bg-subtle)] flex-shrink-0">
          <img src={product.gallery[0].src} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">Build</div>
          <div className="font-display text-base font-semibold text-[var(--ink)] truncate">{product.buildName.split(' — ')[0]}</div>
          <div className="text-xs text-[var(--text-dim)]">See modifications done to this car</div>
        </div>
        <span className="font-display text-xs uppercase tracking-widest text-[var(--accent)] group-hover:translate-x-0.5 transition flex items-center gap-1 flex-shrink-0">
          View <ArrowRight size={13} />
        </span>
      </button>

      {/* Highlights grid */}
      <div className="card p-5">
        <div className="section-label mb-4">Highlights</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          {product.highlights.map((h) => {
            const Icon = highlightIcon[h.icon] || Gauge;
            return (
              <div key={h.key} className="flex items-start gap-2.5" data-testid={`highlight-${h.key}`}>
                {h.swatch ? (
                  <span className="w-8 h-8 rounded-lg border border-[var(--border)] flex-shrink-0" style={{ background: h.swatch }} />
                ) : (
                  <span className="w-8 h-8 rounded-lg bg-[var(--bg-subtle)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
                    <Icon size={14} className="text-[var(--accent)]" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[var(--text-mute)] truncate">{h.label}</div>
                  <div className="font-display text-sm font-semibold text-[var(--ink)] leading-tight truncate">{h.value}</div>
                  {h.unit && <div className="text-[10px] text-[var(--text-dim)] truncate">{h.unit}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Seller card */}
      <div className="card p-5 space-y-3">
        <div className="section-label">Seller</div>
        <div className="flex items-center gap-3">
          <img src={product.seller.avatar} alt={product.seller.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--accent)]" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-display text-base font-semibold">{product.seller.name}</span>
              <ShieldCheck size={14} className="text-[var(--blue)]" />
            </div>
            <div className="text-xs text-[var(--text-mute)] font-mono truncate">{product.seller.handle} · {product.seller.location}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-lg bg-[var(--bg-subtle)]">
            <div className="font-display text-base font-bold">{product.seller.rating}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[var(--text-mute)]">Rating</div>
          </div>
          <div className="p-2 rounded-lg bg-[var(--bg-subtle)]">
            <div className="font-display text-base font-bold">{product.seller.sales}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[var(--text-mute)]">Sales</div>
          </div>
          <div className="p-2 rounded-lg bg-[var(--bg-subtle)]">
            <div className="font-display text-base font-bold">{product.seller.activeListings}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[var(--text-mute)]">Active</div>
          </div>
        </div>
        <button className="btn-ghost w-full" data-testid="contact-seller">
          <MessageCircle size={13} /> Contact Seller
        </button>
      </div>
    </aside>
  );
};

// =========== TAB NAV ========================================
const TABS = [
  { id: 'details',   label: 'Vehicle Details' },
  { id: 'media',     label: 'Media' },
  { id: 'qa',        label: 'Q&A' },
  { id: 'comments',  label: 'Comments' },
  { id: 'purchase',  label: 'Purchase Info' },
  { id: 'terms',     label: 'Conditions of Business' },
];

const TabNav = ({ active, setActive }) => (
  <div className="sticky top-16 z-30 bg-white/90 backdrop-blur-xl border-b border-[var(--border)] mt-12">
    <div className="max-w-[1440px] mx-auto px-6">
      <div className="flex gap-8 overflow-x-auto tab-strip">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            data-testid={`tab-${t.id}`}
            className={`tab-btn ${active === t.id ? 'active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// =========== Vehicle Details panel ==========================
const VehicleDetailsPanel = () => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? product.details : product.details.slice(0, 8);

  return (
    <section id="details" className="space-y-8" data-testid="panel-details">
      {/* Key Selling Points */}
      <div>
        <div className="section-label mb-4">Key Selling Points</div>
        <div className="grid md:grid-cols-2 gap-3">
          {product.keySellingPoints.map((p, i) => (
            <div key={p} className="flex items-start gap-3 card p-4">
              <span className="w-7 h-7 rounded-full bg-black text-white font-display text-xs font-bold flex items-center justify-center flex-shrink-0">
                {String(i + 1).padStart(2, '0')}
              </span>
              <p className="text-sm text-[var(--text-dim)] leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="p-4 rounded-xl bg-[var(--amber-soft)] border border-[var(--amber)]/25 flex items-start gap-3">
        <Info size={16} className="text-[var(--amber)] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--text-dim)]">
          <span className="font-display font-semibold text-[var(--ink)]">Disclaimer</span>{' '}
          — Seller assumes all responsibility for the information provided in this listing.
        </p>
      </div>

      {/* Vehicle Details table */}
      <div>
        <div className="section-label mb-4">Vehicle Details</div>
        <div className="card overflow-hidden">
          <dl className="divide-y divide-[var(--border)]">
            {visible.map((row) => (
              <div key={row.k} className="grid grid-cols-1 md:grid-cols-[220px_1fr] px-5 py-4 gap-1 md:gap-6">
                <dt className="font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--text-mute)]">{row.k}</dt>
                <dd className="text-sm text-[var(--ink)]">{row.v}</dd>
              </div>
            ))}
          </dl>
        </div>
        {product.details.length > 8 && (
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid="details-toggle"
            className="mt-3 flex items-center gap-1.5 text-sm font-display tracking-widest uppercase text-[var(--accent)] hover:text-[var(--ink)] transition"
          >
            {expanded ? <Minus size={14} /> : <Plus size={14} />}
            {expanded ? 'view less' : 'view more'}
          </button>
        )}
      </div>

      {/* Modifications */}
      <div>
        <div className="section-label mb-4">Modifications</div>
        <div className="card p-5 flex flex-col md:flex-row items-stretch md:items-center gap-5">
          <div className="flex items-center gap-3 flex-1">
            <span className="chip chip-accent">
              <Hammer size={11} /> Status · {product.modificationStatus}
            </span>
            <span className="text-sm text-[var(--text-dim)]">
              Full Liberty Walk × Kaido Works wide-aero, RB26DETT refreshed, 480 whp on E85.
            </span>
          </div>
          <Link
            to={`/build/${product.slug}/${product.buildId}`}
            data-testid="modifications-view-build"
            className="btn-primary flex-shrink-0"
          >
            View Build <ArrowRight size={13} />
          </Link>
        </div>
      </div>

      {/* Condition Report */}
      <div>
        <div className="section-label mb-4">Condition Report</div>
        <div className="card p-5 space-y-3">
          <ul className="space-y-2.5">
            {product.conditionReport.map((line) => (
              <li key={line} className="flex items-start gap-2 text-sm text-[var(--text-dim)] leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] mt-2 flex-shrink-0" />
                {line}
              </li>
            ))}
          </ul>
          <div className="pt-3 border-t border-[var(--border)] flex items-center gap-3">
            <FileText size={16} className="text-[var(--accent)]" />
            <span className="font-display text-sm font-semibold text-[var(--ink)]">Condition-Report.pdf</span>
            <span className="font-mono text-[10px] text-[var(--text-mute)] tracking-widest uppercase">2.4 MB</span>
            <button className="ml-auto btn-ghost text-[10px]">Download</button>
          </div>
        </div>
      </div>
    </section>
  );
};

// =========== Media panel ====================================
const MediaPanel = () => {
  const [tab, setTab] = useState('images');
  const subTabs = [
    { id: 'images', label: 'Images', count: product.gallery.length },
    { id: 'videos', label: 'Videos', count: 0 },
    { id: 'build',  label: 'Build', count: 1 },
  ];

  return (
    <section id="media" data-testid="panel-media">
      <div className="section-label mb-4">Media</div>
      <div className="flex items-center gap-2 mb-5">
        {subTabs.map((s) => (
          <button
            key={s.id}
            onClick={() => setTab(s.id)}
            data-testid={`media-tab-${s.id}`}
            className={`chip ${tab === s.id ? 'chip-accent' : ''}`}
          >
            {s.label} <span className="font-mono text-[10px] opacity-70">· {s.count}</span>
          </button>
        ))}
      </div>

      {tab === 'images' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {product.gallery.map((g) => (
            <div key={g.src} className="card overflow-hidden aspect-[3/2] group cursor-pointer">
              <img src={g.src} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            </div>
          ))}
        </div>
      )}

      {tab === 'videos' && (
        <div className="card p-12 text-center">
          <p className="text-sm text-[var(--text-mute)]">No videos uploaded yet for this listing.</p>
        </div>
      )}

      {tab === 'build' && (
        <Link
          to={`/build/${product.slug}/${product.buildId}`}
          data-testid="media-view-build"
          className="card card-hover p-5 flex items-center gap-5 max-w-2xl"
        >
          <div className="w-32 h-24 rounded-xl overflow-hidden bg-[var(--bg-subtle)] flex-shrink-0">
            <img src={product.gallery[0].src} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">Build · {product.year} {product.make} {product.model}</div>
            <div className="font-display text-lg font-bold text-[var(--ink)] leading-tight">{product.buildName}</div>
            <div className="text-xs text-[var(--text-dim)] mt-1">5 tabs · 13 photos · 16 tagged parts · view full breakdown</div>
          </div>
          <span className="font-display text-xs uppercase tracking-widest text-[var(--accent)] flex items-center gap-1 flex-shrink-0">
            View <ArrowRight size={13} />
          </span>
        </Link>
      )}
    </section>
  );
};

// =========== Q&A panel ======================================
const QAPanel = () => (
  <section id="qa" data-testid="panel-qa">
    <div className="section-label mb-4">Questions &amp; Answers</div>
    <div className="card p-5 space-y-3">
      <textarea
        data-testid="qa-input"
        placeholder="Have a question about this listing? The seller usually replies within 24 hours."
        className="w-full min-h-[96px] p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] text-sm focus:outline-none focus:border-[var(--accent)] transition"
      />
      <div className="flex justify-end">
        <button className="btn-primary">Submit Question</button>
      </div>
    </div>
    <div className="mt-6 space-y-4">
      {product.faqs.map((f, i) => (
        <div key={f.q} className="card p-5" data-testid={`faq-${i}`}>
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full bg-black text-white font-display font-bold text-xs flex items-center justify-center flex-shrink-0">Q</span>
            <div className="flex-1">
              <div className="font-display text-base font-semibold text-[var(--ink)]">{f.q}</div>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-3 pl-11">
            <span className="w-8 h-8 rounded-full bg-[var(--accent)] text-white font-display font-bold text-xs flex items-center justify-center flex-shrink-0 -ml-11">A</span>
            <p className="text-sm text-[var(--text-dim)] leading-relaxed">{f.a}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// =========== Comments panel =================================
const CommentsPanel = () => (
  <section id="comments" data-testid="panel-comments">
    <div className="section-label mb-4">Comments ({product.comments})</div>
    <div className="card p-5 space-y-3">
      <textarea
        data-testid="comment-input"
        placeholder="Share your thoughts on this listing…"
        className="w-full min-h-[96px] p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] text-sm focus:outline-none focus:border-[var(--accent)] transition"
      />
      <div className="flex justify-end">
        <button className="btn-primary">Post Comment</button>
      </div>
    </div>
    <p className="mt-6 text-sm text-[var(--text-mute)]">
      View the full comment thread on the{' '}
      <Link to={`/build/${product.slug}/${product.buildId}`} className="text-[var(--accent)] hover:underline">build showcase</Link>.
    </p>
  </section>
);

// =========== Purchase Info panel =============================
const PurchasePanel = () => {
  const [zip, setZip] = useState('');
  return (
    <section id="purchase" className="space-y-8" data-testid="panel-purchase">
      {/* About Seller */}
      <div>
        <div className="section-label mb-4">About Seller</div>
        <div className="card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <img src={product.seller.avatar} alt={product.seller.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[var(--accent)]" />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-display text-lg font-semibold">{product.seller.name}</span>
              <ShieldCheck size={15} className="text-[var(--blue)]" />
            </div>
            <div className="text-xs font-mono text-[var(--text-mute)]">
              {product.seller.handle} · {product.seller.location} · {product.seller.listedSince}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost"><MessageCircle size={13} /> Contact</button>
            <button className="btn-primary">Profile</button>
          </div>
        </div>
      </div>

      {/* Payment */}
      <div>
        <div className="section-label mb-4">Payment</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-5 space-y-2">
            <div className="font-display text-base font-semibold text-[var(--ink)]">bR Premium Fee</div>
            <p className="text-sm text-[var(--text-dim)]">
              {product.payment.premiumFeePct}% of the final winning bid is charged at auction close.
            </p>
            <div className="font-mono text-xs text-[var(--text-mute)]">
              Min ${fmt(product.payment.premiumFeeMin)} · Max ${fmt(product.payment.premiumFeeMax)}
            </div>
          </div>
          <div className="card p-5 space-y-3">
            <div className="font-display text-base font-semibold text-[var(--ink)]">Final Payment</div>
            <p className="text-sm text-[var(--text-dim)]">
              Seller accepts the methods below for the balance after auction close.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {product.payment.methods.map((m) => (
                <span key={m} className="chip text-[10px]"><CreditCard size={10} /> {m}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Buyer Protection */}
      <div className="card p-5 flex items-start gap-4 bg-[var(--accent-soft)] border-[var(--accent)]/25">
        <div className="w-12 h-12 rounded-xl bg-[var(--accent)] text-white flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={22} />
        </div>
        <div>
          <div className="font-display text-base font-bold text-[var(--ink)]">Buyer Protection Guarantee</div>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Your purchase is protected by beRides Escrow — funds release only after you confirm receipt and condition match.
          </p>
        </div>
      </div>

      {/* Shipping */}
      <div>
        <div className="section-label mb-4">Shipping Options</div>
        <div className="grid md:grid-cols-2 gap-4">
          {product.shipping.options.map((o) => (
            <div key={o.title} className="card p-5 space-y-2">
              <div className="flex items-center gap-2">
                <Truck size={15} className="text-[var(--accent)]" />
                <span className="font-display text-base font-semibold text-[var(--ink)]">{o.title}</span>
              </div>
              <p className="text-sm text-[var(--text-dim)]">{o.copy}</p>
              <p className="text-xs text-[var(--text-mute)] font-mono">{o.detail}</p>
            </div>
          ))}
        </div>
        <div className="card p-5 mt-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-[var(--accent)]" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">Delivery ZIP</span>
          </div>
          <input
            data-testid="shipping-zip"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="e.g. 94103"
            className="flex-1 px-4 py-2 rounded-lg border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--accent)] transition"
          />
          <button className="btn-primary">Get Shipping Quote</button>
        </div>
      </div>

      {/* Bid history */}
      <div>
        <div className="section-label mb-4">Recent Bids</div>
        <div className="card overflow-hidden">
          <div className="grid grid-cols-3 px-5 py-3 bg-[var(--bg-subtle)] border-b border-[var(--border)] font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">
            <div>Bidder</div>
            <div>Amount</div>
            <div className="text-right">When</div>
          </div>
          {product.recentBids.map((b) => (
            <div key={b.user + b.amount} className="grid grid-cols-3 px-5 py-3 border-b border-[var(--border)] last:border-0 text-sm" data-testid={`bid-${b.user}`}>
              <div className="font-mono text-[var(--text-dim)]">{b.user}</div>
              <div className="font-display font-semibold text-[var(--ink)]">{usd(b.amount)}</div>
              <div className="text-right font-mono text-xs text-[var(--text-mute)]">{b.when}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =========== Conditions of Business panel ====================
const TermsPanel = () => (
  <section id="terms" data-testid="panel-terms">
    <div className="section-label mb-4">Conditions of Business</div>
    <div className="card p-6 space-y-3">
      <p className="text-sm text-[var(--text-dim)]">
        Please review the following before placing a bid:
      </p>
      <ul className="space-y-2 text-sm">
        {[
          'Guide for Buyers (English)',
          'Conditions of Business for Buyers (English)',
          'Auction Closing & Payment Terms',
          'Vehicle Pickup & Transit Insurance',
          'Buyer Protection Guarantee Coverage',
        ].map((t) => (
          <li key={t} className="flex items-center gap-2 text-[var(--accent)] hover:text-[var(--ink)] transition cursor-pointer">
            <FileText size={13} />
            <span className="font-display tracking-widest text-xs uppercase">{t}</span>
            <ArrowRight size={12} className="ml-auto" />
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// =========== Similar listings ================================
const SimilarListings = () => (
  <section className="max-w-[1440px] mx-auto px-6 mt-16" data-testid="similar-listings">
    <div className="section-label mb-5">Similar Listings</div>
    <div className="grid md:grid-cols-3 gap-5">
      {product.similar.map((s) => (
        <div key={s.id} className="card card-hover overflow-hidden cursor-pointer group">
          <div className="aspect-[3/2] overflow-hidden bg-[var(--bg-subtle)]">
            <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
          </div>
          <div className="p-5">
            <div className="font-display text-lg font-semibold text-[var(--ink)] truncate">{s.title}</div>
            <div className="text-xs font-mono text-[var(--text-mute)] mt-1">{s.location}</div>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="font-display text-2xl font-bold text-[var(--ink)]">{usd(s.bid)}</span>
              <span className="text-xs font-mono text-[var(--text-mute)] tracking-widest uppercase">Current Bid</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// =========== ROOT ===========================================
const VALID_TABS = ['details', 'media', 'qa', 'comments', 'purchase', 'terms'];

const ProductPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'details';
  const [activeTab, setActiveTab] = useState(initialTab);

  // When a deep-link ships a ?tab=… param (e.g. from the Tagged Products
  // "Read more" link), jump the viewport to the tab panel after first paint.
  useEffect(() => {
    if (searchParams.get('tab')) {
      const el = document.querySelector(`[data-testid="panel-${initialTab}"]`);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panels = useMemo(() => ({
    details:   <VehicleDetailsPanel />,
    media:     <MediaPanel />,
    qa:        <QAPanel />,
    comments:  <CommentsPanel />,
    purchase:  <PurchasePanel />,
    terms:     <TermsPanel />,
  }), []);

  return (
    <div className="min-h-screen bg-white pt-16" data-testid="product-page">
      <TopNav />
      <Breadcrumb />
      <TitleBlock />
      <HeroSection />
      <TabNav active={activeTab} setActive={setActiveTab} />
      <div className="max-w-[1440px] mx-auto px-6 py-10">
        {panels[activeTab]}
      </div>
      <SimilarListings />
      <footer className="max-w-[1440px] mx-auto px-6 mt-16 py-8 border-t border-[var(--border)] text-xs font-mono text-[var(--text-mute)] flex flex-col md:flex-row items-center justify-between gap-3">
        <span>© {new Date().getFullYear()} beRides — Built for car people, by car people.</span>
        <div className="flex items-center gap-4">
          <a className="hover:text-[var(--ink)] transition" href="#">Privacy</a>
          <a className="hover:text-[var(--ink)] transition" href="#">Terms</a>
          <a className="hover:text-[var(--ink)] transition" href="#">Help</a>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;
